import { useState, useRef, useEffect, useCallback } from 'preact/hooks';

interface Option {
    value: string | number;
    label: string;
}

interface Props {
    options: Option[];
    value: string | number;
    onChange: (value: string | number) => void;
    placeholder?: string;
    className?: string;
}

export default function SearchableSelect({ options, value, onChange, placeholder = 'Выберите...', className = '' }: Props) {
    const [open, setOpen] = useState(false);
    const [dropUp, setDropUp] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const closingRef = useRef(false);

    // max-h-60 (15rem ≈ 240px) — высота меню для расчёта направления
    const MENU_HEIGHT = 240;

    const sorted = [...options].sort((a, b) => a.label.localeCompare(b.label, 'ru'));
    const filtered = sorted.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
    const selected = options.find(o => Number(o.value) === Number(value));

    useEffect(() => {
        if (open && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [open]);

    const handleSelect = useCallback((val: string | number) => {
        closingRef.current = true;
        onChange(val);
        setOpen(false);
        setSearch('');
        setTimeout(() => { closingRef.current = false; }, 100);
    }, [onChange]);

    const handleClickOutside = useCallback((e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
            setOpen(false);
            setSearch('');
        }
    }, []);

    useEffect(() => {
        if (open) {
            document.addEventListener('click', handleClickOutside, true);
            return () => document.removeEventListener('click', handleClickOutside, true);
        }
    }, [open, handleClickOutside]);

    const toggleOpen = () => {
        if (closingRef.current) return;
        if (!open && containerRef.current) {
            // Решаем направление: если снизу мало места, а сверху больше — открываем вверх
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setDropUp(spaceBelow < MENU_HEIGHT && spaceAbove > spaceBelow);
        }
        setOpen(prev => !prev);
        setSearch('');
    };

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <div
                className={`select select-bordered select-sm w-full flex items-center cursor-pointer ${selected ? 'select-accent' : ''}`}
                onClick={toggleOpen}
            >
                <span className="truncate flex-1">
                    {selected ? selected.label : <span className="text-base-content/40">{placeholder}</span>}
                </span>
            </div>

            {open && (
                <div className={`absolute z-50 w-full bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-60 flex flex-col ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                    <div className="p-2 border-b border-base-300">
                        <input
                            ref={inputRef}
                            type="text"
                            className="input input-bordered input-xs w-full"
                            placeholder="Поиск..."
                            value={search}
                            onInput={e => setSearch((e.target as HTMLInputElement).value)}
                            onClick={e => e.stopPropagation()}
                        />
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        <div
                            className="px-3 py-1.5 text-sm cursor-pointer hover:bg-base-200 text-base-content/50"
                            onClick={(e) => { e.stopPropagation(); handleSelect(''); }}
                        >
                            Не сопоставлять
                        </div>
                        {filtered.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-base-content/40">Ничего не найдено</div>
                        ) : (
                            filtered.map(o => (
                                <div
                                    key={o.value}
                                    className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-base-200 ${Number(o.value) === Number(value) ? 'bg-accent/10 font-medium' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); handleSelect(o.value); }}
                                >
                                    {o.label}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
